import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["themeToggleNotice", "exactColorsDisabledNotice", "exactColorsDescription", "exactColorsEnabledNotice"];

  connect() {
    // Saving the form triggers a Turbo morph refresh, which re-renders the
    // checkboxes to their saved state without reconnecting this controller.
    // Re-sync after every morph so the disabled states and notices match.
    this.boundSync = this.sync.bind(this);
    document.addEventListener("turbo:morph", this.boundSync);
    this.sync();
  }

  disconnect() {
    document.removeEventListener("turbo:morph", this.boundSync);
  }

  enable(event) {
    this.sendEvent("enableCustomTheme", { checked: event.target.checked });
    // While custom colors were off the exact colors setting was hidden, so a
    // theme toggle enabled in the meantime wins over a lingering checkmark.
    if (event.target.checked && this.themeToggle?.checked) this.forceExactColorsOff();
    this.sync();
  }

  enableExact(event) {
    this.sendEvent("enableExactColors", { checked: event.target.checked });
    this.sync();
  }

  toggleExclusive(event) {
    if (event.target.checked) this.forceExactColorsOff();
    this.sync();
  }

  // The webinar theme toggle and exact theme colors are mutually exclusive,
  // but exact colors only take effect while custom theme colors are on: the
  // setting is hidden otherwise, so it must not block the theme toggle.
  sync() {
    const exactColorsApplied = Boolean(this.exactColors?.checked && this.customColors?.checked);
    const themeToggleOn = Boolean(this.themeToggle?.checked);

    if (this.themeToggle) this.themeToggle.disabled = exactColorsApplied;
    if (this.exactColors) this.exactColors.disabled = themeToggleOn || this.customColorsLocked;

    if (this.hasThemeToggleNoticeTarget) this.themeToggleNoticeTarget.hidden = !exactColorsApplied;
    if (this.hasExactColorsEnabledNoticeTarget) this.exactColorsEnabledNoticeTarget.hidden = !exactColorsApplied;
    if (this.hasExactColorsDisabledNoticeTarget) {
      this.exactColorsDisabledNoticeTarget.hidden = !themeToggleOn;
      this.exactColorsDescriptionTarget.hidden = themeToggleOn;
    }
  }

  forceExactColorsOff() {
    if (!this.exactColors?.checked) return;

    this.exactColors.checked = false;
    this.sendEvent("enableExactColors", { checked: false });
  }

  get customColors() {
    return document.getElementById("branding_theme_colors_active");
  }

  // Accounts without the custom colors feature get the whole premium section
  // disabled server side, so it must never be re-enabled by a sync.
  get customColorsLocked() {
    return Boolean(this.customColors?.disabled);
  }

  get themeToggle() {
    return document.getElementById("branding_webinar_theme_toggle");
  }

  get exactColors() {
    return document.getElementById("branding_apply_exact_theme_colors");
  }

  update(event) {
    let value = event.target.value;
    let type = event.target.dataset.type;

    if (value.length === 7 && value[0] === "#") {
      this.sendEvent("changeColor", {type: type, value: value});
    }
  }

  sendEvent(eventName, object) {
    theme_colors_embed_dark("themePreviewEvent", eventName, object);
    theme_colors_embed_light("themePreviewEvent", eventName, object);
  }
}
